import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { Content, ButtonGrid } from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  releaseStageOptions,
  Software,
  SoftwareDetailsSuccess,
  SoftwareReleaseStageAttribute,
} from 'models';
import { useParams } from 'react-router';
import Loader from 'components/UI/Loader';
import SelectField from 'components/UI/SelectField';
import { showErrorToast } from 'utils/toast';
import {
  softwareDetailsSelector,
  updateReleaseStageForSoftware,
} from 'store/softwareState/softwareState';

type Props = {
  software: Software | null;
  open: boolean;
  onClose: () => void;
};

const EditSoftwareModal: FC<Props> = ({ software, open, onClose }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm();
  const { id } = useParams();

  const {
    isLoading,
    error,
    data,
    isUpdatedSuccessfully,
  }: SoftwareDetailsSuccess = useAppSelector(softwareDetailsSelector);

  useEffect(() => {
    if (error) {
      showErrorToast(
        `${intl.formatMessage({
          id: 'software_edit.error',
        })}. ${error}`
      );
    }
    if (isUpdatedSuccessfully) {
      console.log('software updated close window');
      onClose();
    }
  }, [data, dispatch, error, id, intl, isUpdatedSuccessfully, onClose]);

  // Select option
  const onSelectField = useCallback(
    (name: string) => (value: string) => {
      setValue(name, value);
    },
    [setValue]
  );

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      if (id) {
        dispatch(updateReleaseStageForSoftware(values, id));
      }
    },
    [dispatch, id]
  );

  const loadingSpinner = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
  }, [isLoading]);

  return (
    <EditModal title="software.edit_title" open={open} close={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <SelectField
            name={SoftwareReleaseStageAttribute.RELEASE_STAGE}
            error={errors.releaseStage}
            register={register(SoftwareReleaseStageAttribute.RELEASE_STAGE, {
              required: { value: true, message: 'required' },
            })}
            onSelect={onSelectField(
              SoftwareReleaseStageAttribute.RELEASE_STAGE
            )}
            placeholder={intl.formatMessage({ id: 'select.placeholder' })}
            label={intl.formatMessage({ id: 'software.releaseStage' })}
            // defaultValue={watchReleaseStage}
            defaultValue={software?.attributes.releaseStage}
            options={releaseStageOptions}
          />
        </Content>
        <ButtonGrid>
          {loadingSpinner}
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="modal.btn_close" />
          </Button>
          <Button submit id="update_software_btn">
            <FormattedMessage id="software.btn_save" />
          </Button>
        </ButtonGrid>
      </form>
    </EditModal>
  );
};

export default EditSoftwareModal;
